import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where, 
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { bookTimeSlot } from './availabilityService';

const APPOINTMENTS_COLLECTION = 'appointments';

/**
 * Convert Firestore timestamp to JavaScript Date
 */
const convertTimestamp = (timestamp) => {
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  return timestamp;
};

/**
 * Convert Firestore document to appointment object
 */
const convertDocToAppointment = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    createdAt: convertTimestamp(data.createdAt),
    appointmentDate: convertTimestamp(data.appointmentDate),
  };
};

/**
 * Create a new appointment
 */
export const createAppointment = async (appointmentData) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const appointmentsRef = collection(db, APPOINTMENTS_COLLECTION);
    const newAppointment = {
      ...appointmentData,
      status: appointmentData.status || 'pending', // pending, confirmed, cancelled, completed
      source: appointmentData.source || 'website', // website or google_calendar
      googleCalendarEventId: appointmentData.googleCalendarEventId || null,
      createdAt: Timestamp.now(),
      appointmentDate: Timestamp.fromDate(new Date(appointmentData.appointmentDate)),
    };

    const docRef = await addDoc(appointmentsRef, newAppointment);

    if (appointmentData.appointmentTime) {
      await bookTimeSlot(appointmentData.appointmentDate, appointmentData.appointmentTime);
    }

    return { id: docRef.id, ...newAppointment };
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
};

/**
 * Get all appointments, ordered by appointment date (newest first)
 */
export const getAllAppointments = async () => {
  try {
    if (!db) {
      console.error('Firestore database is not initialized.');
      return [];
    }

    const appointmentsRef = collection(db, APPOINTMENTS_COLLECTION);
    
    try {
      const q = query(appointmentsRef, orderBy('appointmentDate', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertDocToAppointment(doc));
    } catch (queryError) {
      // If orderBy fails, try without orderBy
      if (queryError.code === 'failed-precondition' || queryError.code === 9) {
        console.warn('OrderBy query requires an index. Fetching without order.');
        const querySnapshot = await getDocs(appointmentsRef);
        const appointments = querySnapshot.docs.map(doc => convertDocToAppointment(doc));
        return appointments.sort((a, b) => {
          const dateA = a.appointmentDate instanceof Date ? a.appointmentDate : new Date(a.appointmentDate);
          const dateB = b.appointmentDate instanceof Date ? b.appointmentDate : new Date(b.appointmentDate);
          return dateB - dateA;
        });
      }
      throw queryError;
    }
  } catch (error) {
    console.error('Error fetching appointments:', error);
    return [];
  }
};

/**
 * Get appointments by status
 */
export const getAppointmentsByStatus = async (status) => {
  try {
    if (!db) {
      return [];
    }

    const appointmentsRef = collection(db, APPOINTMENTS_COLLECTION);
    const q = query(
      appointmentsRef, 
      where('status', '==', status),
      orderBy('appointmentDate', 'desc')
    );
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertDocToAppointment(doc));
    } catch (queryError) {
      // Fallback if index doesn't exist
      if (queryError.code === 'failed-precondition') {
        const allAppointments = await getAllAppointments();
        return allAppointments.filter(apt => apt.status === status);
      }
      throw queryError;
    }
  } catch (error) {
    console.error('Error fetching appointments by status:', error);
    return [];
  }
};

/**
 * Get appointment by ID
 */
export const getAppointmentById = async (id) => {
  try {
    if (!db) {
      return null;
    }

    const appointmentRef = doc(db, APPOINTMENTS_COLLECTION, id);
    const appointmentSnap = await getDoc(appointmentRef);
    
    if (!appointmentSnap.exists()) {
      return null;
    }
    
    return convertDocToAppointment(appointmentSnap);
  } catch (error) {
    console.error('Error fetching appointment by ID:', error);
    return null;
  }
};

/**
 * Update appointment status
 */
export const updateAppointmentStatus = async (appointmentId, status) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }

    const appointmentRef = doc(db, APPOINTMENTS_COLLECTION, appointmentId);
    await updateDoc(appointmentRef, {
      status: status,
      updatedAt: Timestamp.now()
    });
    
    return await getAppointmentById(appointmentId);
  } catch (error) {
    console.error('Error updating appointment status:', error);
    throw error;
  }
};

/**
 * Update appointment
 */
export const updateAppointment = async (appointmentId, appointmentData) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const appointmentRef = doc(db, APPOINTMENTS_COLLECTION, appointmentId);
    const updateData = {
      ...appointmentData,
      updatedAt: Timestamp.now()
    };

    if (appointmentData.appointmentDate) {
      updateData.appointmentDate = Timestamp.fromDate(new Date(appointmentData.appointmentDate));
    }

    await updateDoc(appointmentRef, updateData);
    return await getAppointmentById(appointmentId);
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

/**
 * Delete appointment
 */
export const deleteAppointment = async (appointmentId) => {
  try {
    if (!db) {
      throw new Error('Firestore database is not initialized');
    }

    const appointmentRef = doc(db, APPOINTMENTS_COLLECTION, appointmentId);
    await deleteDoc(appointmentRef);
    return true;
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

/**
 * Get available time slots for a date (helper function for frontend)
 */
export const getAvailableTimeSlots = (date) => {
  // Generate time slots from 8 AM to 8 PM, every 30 minutes
  const slots = [];
  const startHour = 8;
  const endHour = 20;
  
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(time);
    }
  }
  
  return slots;
};

